// Global Instructions Rule Applied!
import React, { useState, type ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { selectTabs, selectActiveTabKey, type Tab } from '../../../store/slices/tabSlice'

// Import page components
import DealPipelinePage from '../../../app_modules/deals/components/pipelines/DealPipelinePage.tsx'
import DealExecutionBoard from '../../../app_modules/deal_execution/components/DealExecutionBoard.tsx'
import DealsPage from '../../../app_modules/deals/components/tables/index.tsx'
import FullDealForm from '../../../app_modules/deals/components/forms/Full.tsx'
import ContactsPage from '../../../app_modules/contacts/components/tables/index.tsx'
import FullContactForm from '../../../app_modules/contacts/components/forms/Full.tsx'
import CompaniesPage from '../../../app_modules/companies/components/tables/index.tsx'
import FullCompanyForm from '../../../app_modules/companies/components/forms/Full.tsx'
import FundraisingPage from '../../../app_modules/fundraising/components/tables/index.tsx'
import FullProspectForm from '../../../app_modules/fundraising/components/forms/Full.tsx'
import FundraisingKanban from '../../../app_modules/fundraising/components/pipelines/kanbans/fundraising/index.tsx'
import FullRoundsForm from '../../../app_modules/rounds/forms/Full.jsx'
import FullTrancheForm from '../../../app_modules/tranches/forms/Full.tsx'
import TranchesPage from '../../../app_modules/tranches/index.tsx'
import FullSecurityForm from '../../../app_modules/securities/forms/Full.tsx'
import FullInvestorForm from '../../../app_modules/investors/components/forms/Full.tsx'
import FullCommitmentForm from '../../../app_modules/commitments/components/forms/Full.tsx'
import FullConsolidatedCommitmentForm from '../../../app_modules/commitments/components/forms/ConsolidatedFull.tsx'
import DownloadsPage from '../../../app_modules/downloads/index.tsx'
import DocumentReportsPage from '../../../app_modules/document_reports/index.tsx'
import FundsPage from '../../../app_modules/funds/index.tsx'
import InvestmentsPage from '../../../app_modules/investments/index.tsx'
import FullFundForm from '../../../app_modules/funds/components/forms/Full.tsx'
import FundTransactionsPage from '../../../app_modules/fund_transactions/index.tsx'
import FullFundTransactionForm from '../../../app_modules/fund_transactions/components/forms/Full.tsx'
import FeeBookingPage from '../../../app_modules/fund_transactions/components/FeeBookingPage.tsx'
import InterestAccrualsPage from '../../../app_modules/interest_accruals/components/InterestAccrualsPage.tsx'
import AgentsPage from '../../../app_modules/internal_agents/index.tsx'
import ConsolidatedFundsPage from '../../../app_modules/fund_groups/index.tsx'
import FundGroupPage from '../../../app_modules/fund_groups/components/FundGroupPage.tsx'
import AssetTransactionsPage from '../../../app_modules/asset_transactions/index.tsx'
import FullAssetTransactionForm from '../../../app_modules/asset_transactions/components/forms/Full.tsx'
import CapitalCallsPage from '../../../app_modules/capital_calls/index.tsx'
import JournalEntriesPage from '../../../app_modules/journal_entries/index.tsx'
import FullJournalEntryForm from '../../../app_modules/journal_entries/components/forms/Full.tsx'
import InvestorStatementRunsPage from '../../../app_modules/investor_statements/index.tsx'
import FullInvestorStatementRunForm from '../../../app_modules/investor_statements/components/forms/Full.tsx'
import FullCapitalCallForm from '../../../app_modules/capital_calls/components/forms/Full.tsx'
import FullDistributionForm from '../../../app_modules/distributions/components/forms/Full.tsx'
import CallPositionPage from '../../../app_modules/capital_calls/components/CallPositionPage.tsx'
import TopicManagementPage from '../../../app_modules/distribution_lists/index.tsx'
import TopicSubscriptionsPage from '../../../app_modules/distribution_lists/components/TopicSubscriptionsPage.tsx'
import DistributionListsPage from '../../../app_modules/distribution_lists/components/DistributionListsPage.tsx'
import FullDistributionListForm from '../../../app_modules/distribution_lists/components/forms/Full.tsx'
import SendCommunicationPage from '../../../app_modules/distribution_lists/components/SendCommunicationPage.tsx'
import BulkAddProspectsPage from '../../../app_modules/fundraising/components/bulk_add/BulkAddProspectsPage.tsx'
import LpAllocationPage from '../../../app_modules/relationships/components/LpAllocationPage.tsx'
import LpsPage from '../../../app_modules/lps/components/LpsPage.tsx'
import MyDraftsPage from '../../../app_modules/relationships/components/MyDraftsPage.tsx'
import PeriodHighlightsPage from '../../../app_modules/period_highlights/components/PeriodHighlightsPage.tsx'
import HighlightSetForm from '../../../app_modules/period_highlights/components/HighlightSetForm.tsx'
import EmailCampaignsPage from '../../../app_modules/email/components/EmailCampaignsPage.tsx'
import FullEmailCampaignForm from '../../../app_modules/email/components/forms/Full.tsx'
import TemplateFull from '../../../app_modules/email/components/forms/TemplateFull.tsx'
import EmailSettingsPage from '../../../app_modules/email_settings/index.tsx'
import AgentAccessPage from '../../../app_modules/agent_access/index.tsx'
import ConfigurationPage from '../../../app_modules/configuration/index.tsx'
import ConfigSectionPage from '../../../app_modules/configuration/components/ConfigSectionPage.tsx'
import { CONFIG_MODULES_BY_KEY } from '../../../app_modules/configuration/configModules.ts'
import ComingSoonPage from '../../components/ComingSoon.jsx'
import FileViewer from '../../components/FileViewer/index.tsx'

/**
 * Tab Content Wrapper - Manages rendering content for each tab
 *
 * This component uses the Redux tab state to render the appropriate content
 * for each tab without unmounting components when switching between tabs.
 * This preserves component state and improves performance.
 */
const TabContentWrapper = React.memo(function TabContentWrapper() {
  const tabs = useSelector(selectTabs)
  const activeTabKey = useSelector(selectActiveTabKey)

  // Keep track of which tabs have been loaded to maintain state persistence
  const [loadedTabs, setLoadedTabs] = useState(new Set<string>())

  // The content for one tab. Each branch returns its element directly so
  // every page/form is type-checked against its own props.
  const getTabContent = (tab: Tab): ReactElement => {
    const formProps = { recordId: tab.recordId, isNew: tab.isNew }
    const isFullForm = tab.type === 'form' && tab.isFullForm

    // Configuration AREA modules ('config-*') all render the same wrapper
    // around one CONFIG_SECTIONS panel — resolved from the registry rather
    // than one switch case per panel.
    const configModule = CONFIG_MODULES_BY_KEY[tab.module]
    if (configModule) {
      return <ConfigSectionPage sectionKey={configModule.sectionKey} />
    }

    // Determine which component to render based on tab configuration
    switch (tab.module) {
      case 'deal-pipeline':
        // Tabbed page (Board | Meetings) since the pipeline meeting agent —
        // the kanban renders unchanged inside the Board tab.
        return <DealPipelinePage />

      case 'deal-execution':
        return <DealExecutionBoard />

      // For form tabs, render the full form; a table tab (or a drawer
      // route) renders the module page, which handles drawer state itself.
      case 'deals':
        return isFullForm ? <FullDealForm {...formProps} /> : <DealsPage />

      case 'contacts':
        return isFullForm ? <FullContactForm {...formProps} /> : <ContactsPage />

      case 'companies':
        return isFullForm ? <FullCompanyForm {...formProps} /> : <CompaniesPage />

      case 'fundraising':
        return isFullForm ? <FullProspectForm {...formProps} /> : <FundraisingPage />

      case 'fundraising-pipeline':
        return isFullForm ? <FullProspectForm {...formProps} /> : <FundraisingKanban />

      case 'rounds':
        // Rounds have a full form only; the list page was removed as dead
        // code (no menu entry, and it crashed on a store shape it never had).
        return isFullForm ? <FullRoundsForm {...formProps} /> : <ComingSoonPage currentPath={tab.path} />

      case 'tranches':
        return isFullForm ? <FullTrancheForm {...formProps} /> : <TranchesPage />

      case 'securities':
        // Form-only module (no grid yet) — securities are reached from the
        // links on transactions/tranches, not from a table tab.
        return isFullForm ? <FullSecurityForm {...formProps} /> : <ComingSoonPage currentPath={tab.path} />

      case 'investors':
        // Form-only module — an investor entity is reached from the investor
        // links of the Investors grids and the account's Funds tab.
        return isFullForm ? <FullInvestorForm {...formProps} /> : <ComingSoonPage currentPath={tab.path} />

      case 'commitments':
        // Form-only module — one commitment to a fund (or the New commitment
        // form), reached from the Investors grids, the Investor form and the
        // account's Funds tab.
        return isFullForm ? <FullCommitmentForm {...formProps} /> : <ComingSoonPage currentPath={tab.path} />

      case 'consolidated-commitments':
        // Form-only module — one commitment to a consolidated fund.
        return isFullForm ? (
          <FullConsolidatedCommitmentForm recordId={formProps.recordId} />
        ) : (
          <ComingSoonPage currentPath={tab.path} />
        )

      case 'asset-transactions':
        return isFullForm ? <FullAssetTransactionForm {...formProps} /> : <AssetTransactionsPage />

      case 'downloads':
        return <DownloadsPage />

      case 'reports':
        return <DocumentReportsPage />

      case 'funds':
        return isFullForm ? <FullFundForm {...formProps} /> : <FundsPage />

      case 'investments':
        return <InvestmentsPage />

      case 'consolidated-funds':
        return tab.type === 'form' ? (
          <FundGroupPage recordId={tab.recordId ?? null} isNew={tab.isNew} />
        ) : (
          <ConsolidatedFundsPage />
        )

      case 'fee-booking':
        return <FeeBookingPage />

      case 'interest-accruals':
        return <InterestAccrualsPage />

      case 'agents':
        return <AgentsPage />

      case 'capital-call-position':
        return <CallPositionPage />

      case 'fund-transactions':
        return isFullForm ? <FullFundTransactionForm {...formProps} /> : <FundTransactionsPage />

      case 'capital-calls':
        return isFullForm ? <FullCapitalCallForm {...formProps} /> : <CapitalCallsPage />

      case 'distributions':
        // the form only: a distribution is reached from its fund (phase 3 has
        // no distributions grid); the module tab falls back to the hub
        return isFullForm ? <FullDistributionForm {...formProps} /> : <FundTransactionsPage />

      case 'journal-entries':
        return isFullForm ? <FullJournalEntryForm {...formProps} /> : <JournalEntriesPage />

      case 'investor-statements':
        return isFullForm ? <FullInvestorStatementRunForm {...formProps} /> : <InvestorStatementRunsPage />

      case 'topics':
        return <TopicManagementPage />

      case 'topic-subscriptions':
        return <TopicSubscriptionsPage />

      case 'distribution-lists':
        return tab.type === 'form' ? <FullDistributionListForm {...formProps} /> : <DistributionListsPage />

      case 'send-communication':
        return <SendCommunicationPage />

      case 'bulk-add-prospects':
        return <BulkAddProspectsPage />

      // Fundraising → LP Allocation (who covers which LP) and MY WORKSPACE →
      // My Drafts (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 0).
      case 'lp-allocation':
        return <LpAllocationPage />

      // FUND MANAGEMENT → LPs: team name / team-name-set over the same LP set.
      case 'lps':
        return <LpsPage />

      case 'my-drafts':
        return <MyDraftsPage />

      // Reporting → Period Highlights (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
      // Phase 1): the list page, and a set as a form tab
      // (/period-highlights/forms/full/:id — the distribution-lists idiom).
      case 'period-highlights':
        return tab.type === 'form' ? <HighlightSetForm recordId={tab.recordId} /> : <PeriodHighlightsPage />

      case 'email':
        return tab.type === 'form' ? <FullEmailCampaignForm {...formProps} /> : <EmailCampaignsPage />

      case 'email-design-templates':
        // Bare /email-design-templates URL — render the same Email page
        // but request the Templates tab. The page reads its initialTab
        // from the prop.
        return tab.type === 'form' ? <TemplateFull {...formProps} /> : <EmailCampaignsPage initialTab='templates' />

      case 'email-settings':
        return <EmailSettingsPage />

      // 'agent-access' is the screen's former route, kept for one release so
      // existing bookmarks still open. 'my-agents' is the live one.
      case 'my-agents':
      case 'agent-access':
        return <AgentAccessPage />

      case 'configuration':
        // Fund Management area copy: Pipelines config lives in Operations
        return <ConfigurationPage hiddenSections={['pipelines']} />

      case 'configuration-operations':
        return <ConfigurationPage />

      case 'file-viewer':
        return tab.type === 'file-viewer' ? (
          <FileViewer fileUrl={tab.fileUrl} fileName={tab.fileName} />
        ) : (
          <ComingSoonPage />
        )

      case 'dashboard':
      case 'fund-management':
      case 'operations':
      case 'settings':
        // For coming soon pages, pass the current path
        return (
          <ComingSoonPage
            currentPath={tab.path}
            title={`${tab.title} Coming Soon`}
            message={`The ${tab.title} module is currently under development and will be available soon.`}
          />
        )

      default:
        return <ComingSoonPage currentPath={tab.path} />
    }
  }

  // Mark active tab as loaded when it becomes active
  React.useEffect(() => {
    if (activeTabKey && !loadedTabs.has(activeTabKey)) {
      setLoadedTabs((prev) => new Set([...prev, activeTabKey]))
    }
  }, [activeTabKey, loadedTabs])

  // Only render loaded tabs to maintain state, but show only the active one
  return (
    <div className='tab-content-wrapper h-full'>
      {tabs.map((tab) => {
        // Only render tabs that have been visited (to maintain state) or the currently active tab
        const shouldRender = loadedTabs.has(tab.key) || tab.key === activeTabKey
        if (!shouldRender) return null

        const isActive = tab.key === activeTabKey

        return (
          <div
            key={tab.key}
            className='tab-content-panel'
            style={{
              display: isActive ? 'block' : 'none',
              height: '100%',
              width: '100%'
            }}
          >
            {getTabContent(tab)}
          </div>
        )
      })}
    </div>
  )
})

export { TabContentWrapper }
